// src/pages/CampaignsPage.tsx
import React, { useState } from "react";
import { Plus, Search, Filter, Play, Pause, BarChart2, Copy, TrendingUp, TrendingDown, Megaphone } from "lucide-react";
import { clsx } from "clsx";
import CampaignWizard from "@/components/ui/CampaignWizard";
import { CAMPAIGNS, BRAND_SNAPSHOTS } from "@/data";
import type { Campaign } from "@/types";

const STATUS_STYLES: Record<string, string> = { active: "badge-green", paused: "badge-yellow", draft: "badge-slate", completed: "badge-navy" };
const OBJ_STYLES: Record<string, string> = { awareness: "badge-blue", consideration: "badge-cyan", conversion: "badge-green", retention: "badge-navy" };
const CHANNEL_LABELS: Record<string, string> = { oo: "O&O", display: "Display", ctv: "CTV", video: "Video", social: "Social" };

function SparkBar({ value, max, color }: { value: number; max: number; color: string }) {
  return (
    <div className="w-full bg-gray-100 rounded-full h-1.5 mt-1">
      <div className={clsx("h-1.5 rounded-full transition-all duration-500", color)} style={{ width: `${Math.min((value/max)*100,100)}%` }} />
    </div>
  );
}

export default function CampaignsPage() {
  const [showWizard, setShowWizard] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState<Campaign | null>(null);

  const filtered = CAMPAIGNS.filter(c => {
    const matchSearch = c.name.toLowerCase().includes(search.toLowerCase()) || c.advertiser.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === "all" || c.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const active   = CAMPAIGNS.filter(c => c.status === "active");
  const totalSpend = active.reduce((s, c) => s + c.spent, 0);
  const totalImpr  = active.reduce((s, c) => s + c.impressions, 0);
  const avgRoas    = active.filter(c => c.roas > 0).reduce((s, c, _, a) => s + c.roas / a.length, 0);
  return (
    <div className="space-y-4 animate-fade-in">
      {showWizard && <CampaignWizard onClose={() => setShowWizard(false)} />}
      <div className="flex items-center justify-between">
        <div className="page-header mb-0">
          <h1 className="page-title">Campaigns</h1>
          <p className="page-subtitle">Manage and monitor all active campaigns</p>
        </div>
        <button onClick={() => setShowWizard(true)} className="btn-primary flex items-center gap-2">
          <Plus size={14} /> New Campaign
        </button>
      </div>
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: "Active Campaigns",  value: active.length.toString(),               delta: 12.5 },
          { label: "Total Spend MTD",   value: `$${(totalSpend/1_000_000).toFixed(1)}M`, delta: 14.3 },
          { label: "Total Impressions", value: `${(totalImpr/1_000_000).toFixed(0)}M`,   delta: 9.8  },
          { label: "Avg ROAS",          value: `${avgRoas.toFixed(1)}x`,                 delta: 6.2  },
        ].map((kpi, i) => (
          <div key={i} className="kpi-card">
            <p className="kpi-label">{kpi.label}</p>
            <p className="kpi-value">{kpi.value}</p>
            <div className="flex items-center gap-1 mt-1">
              <TrendingUp size={11} className="text-emerald-500" />
              <span className="kpi-delta-up">+{kpi.delta}% vs last month</span>
            </div>
          </div>
        ))}
      </div>
      <div className="card">
        <h3 className="text-sm font-semibold text-navy mb-3">Performance by Advertiser</h3>
        <div className="grid grid-cols-5 gap-4">
          {BRAND_SNAPSHOTS.map(b => (
            <div key={b.brandId} className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-xs font-semibold text-navy truncate">{b.brandName}</p>
              <p className="text-lg font-bold text-navy tabular mt-1">{b.avgRoas.toFixed(1)}x</p>
              <p className="text-2xs text-slate">ROAS</p>
              <SparkBar value={b.totalSpend} max={6_000_000} color="bg-blue" />
              <p className="text-2xs text-slate mt-1">${(b.totalSpend/1_000_000).toFixed(1)}M spend</p>
              <div className="flex items-center justify-center gap-0.5 mt-1">
                {b.momSpendChange >= 0 ? <TrendingUp size={10} className="text-emerald-500" /> : <TrendingDown size={10} className="text-red-500" />}
                <span className={clsx("text-2xs font-medium", b.momSpendChange >= 0 ? "text-emerald-600" : "text-red-500")}>
                  {b.momSpendChange >= 0 ? "+" : ""}{b.momSpendChange}%
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate" />
              <input className="input pl-9 w-64" placeholder="Search campaigns..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <div className="flex gap-1">
              {["all","active","paused","draft","completed"].map(s => (
                <button key={s} onClick={() => setStatusFilter(s)} className={clsx("btn-sm btn capitalize", statusFilter === s ? "btn-primary" : "btn-secondary")}>{s}</button>
              ))}
            </div>
          </div>
          <button className="btn-secondary btn-sm flex items-center gap-1.5"><Filter size={12} /> Filter</button>
        </div>
        <table className="table-base">
          <thead><tr><th>Campaign</th><th>Objective</th><th>Channels</th><th>Budget</th><th>Spent</th><th>Impressions</th><th>Conversions</th><th>ROAS</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {filtered.map(c => {
              const pct = c.budget > 0 ? (c.spent/c.budget)*100 : 0;
              return (
                <tr key={c.id} className="cursor-pointer" onClick={() => setSelected(c === selected ? null : c)}>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-lg bg-blue/10 flex items-center justify-center flex-shrink-0">
                        <Megaphone size={12} className="text-blue" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800 max-w-[180px] truncate">{c.name}</p>
                        <p className="text-xs text-slate">{c.advertiser}</p>
                      </div>
                    </div>
                  </td>
                  <td><span className={clsx("badge", OBJ_STYLES[c.objective])}>{c.objective}</span></td>
                  <td>
                    <div className="flex gap-1 flex-wrap">
                      {c.channels.slice(0,2).map(ch => <span key={ch} className="badge badge-slate">{CHANNEL_LABELS[ch]}</span>)}
                      {c.channels.length > 2 && <span className="badge badge-slate">+{c.channels.length-2}</span>}
                    </div>
                  </td>
                  <td className="tabular">${(c.budget/1000).toFixed(0)}K</td>
                  <td>
                    <span className="tabular font-medium text-gray-800">${(c.spent/1000).toFixed(0)}K</span>
                    <SparkBar value={c.spent} max={c.budget} color={pct > 90 ? "bg-red-400" : pct > 70 ? "bg-amber-400" : "bg-blue"} />
                    <span className="text-2xs text-slate">{pct.toFixed(0)}% of budget</span>
                  </td>
                  <td className="tabular">{c.impressions > 0 ? `${(c.impressions/1_000_000).toFixed(1)}M` : "—"}</td>
                  <td className="tabular">{c.conversions > 0 ? c.conversions.toLocaleString() : "—"}</td>
                  <td>{c.roas > 0 ? <span className={clsx("font-semibold tabular", c.roas >= 5 ? "text-emerald-600" : "text-amber-600")}>{c.roas.toFixed(1)}x</span> : <span className="text-slate">—</span>}</td>
                  <td><span className={clsx("badge", STATUS_STYLES[c.status])}>{c.status}</span></td>
                  <td>
                    <div className="flex gap-1">
                      {c.status === "active" && <button className="btn-ghost btn-sm p-1.5"><Pause size={12} /></button>}
                      {(c.status === "paused" || c.status === "draft") && <button className="btn-ghost btn-sm p-1.5"><Play size={12} /></button>}
                      <button className="btn-ghost btn-sm p-1.5"><BarChart2 size={12} /></button>
                      <button className="btn-ghost btn-sm p-1.5"><Copy size={12} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
